import type { GtmBrain } from "../api/GtmApi";

export type SignalDocId = "hiring" | "stack";

export type SignalDoc = {
  id: SignalDocId;
  label: string;
  description: string;
  icon: SignalDocId;
  filled: number;
  total: number;
};

function hasText(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

function filledCount(checks: boolean[]): { filled: number; total: number } {
  return { filled: checks.filter(Boolean).length, total: checks.length };
}

/** Normalize signalsJson into a string map (keys lowercased). */
export function parseSignalsMap(raw?: string | null): Record<string, string> {
  const text = (raw || "").trim();
  if (!text) return {};
  try {
    const parsed = JSON.parse(text) as unknown;
    if (Array.isArray(parsed)) {
      const out: Record<string, string> = {};
      parsed.forEach((item, i) => {
        if (typeof item === "string" && item.trim()) {
          const key = item.toLowerCase().includes("hir")
            ? "hiring"
            : item.toLowerCase().includes("stack")
              ? "stack"
              : `signal-${i}`;
          out[key] = item.trim();
        } else if (item && typeof item === "object") {
          const rec = item as Record<string, unknown>;
          const name = String(rec.name ?? rec.type ?? rec.key ?? "").trim().toLowerCase();
          const body = String(rec.value ?? rec.notes ?? rec.description ?? rec.signal ?? "").trim();
          if (name && body) out[name] = body;
        }
      });
      return out;
    }
    if (parsed && typeof parsed === "object") {
      const out: Record<string, string> = {};
      Object.entries(parsed as Record<string, unknown>).forEach(([k, v]) => {
        if (v == null) return;
        const body =
          typeof v === "string"
            ? v.trim()
            : typeof v === "boolean"
              ? v
                ? "Yes"
                : ""
              : typeof v === "number"
                ? String(v)
                : JSON.stringify(v);
        if (body) out[k.trim().toLowerCase()] = body;
      });
      return out;
    }
  } catch {
    if (text) return { notes: text };
  }
  return {};
}

export function signalBody(brain: GtmBrain, id: SignalDocId): string {
  const map = parseSignalsMap(brain.signalsJson);
  if (hasText(map[id])) return map[id];
  // Common aliases from AI suggestions
  if (id === "hiring") {
    return map.hire || map.hiring_signals || map["hiring signals"] || "";
  }
  return map.tech || map.technology || map["tech stack"] || map.techstack || "";
}

export function signalSections(
  brain: GtmBrain,
  id: SignalDocId
): Array<{ id: string; label: string; body: string }> {
  const label = id === "hiring" ? "Hiring signals" : "Stack signals";
  return [{ id: "body", label, body: signalBody(brain, id) }];
}

export function signalDocs(brain: GtmBrain): SignalDoc[] {
  const hiring = signalBody(brain, "hiring");
  const stack = signalBody(brain, "stack");
  const hiringFill = filledCount([hasText(hiring)]);
  const stackFill = filledCount([hasText(stack)]);

  return [
    {
      id: "hiring",
      label: "Hiring",
      description: "Roles and hiring patterns that mark a good fit.",
      icon: "hiring",
      filled: hiringFill.filled,
      total: hiringFill.total,
    },
    {
      id: "stack",
      label: "Stack",
      description: "Tools and stack signals that mark a good fit.",
      icon: "stack",
      filled: stackFill.filled,
      total: stackFill.total,
    },
  ];
}

export const SIGNALS_SUBNAV = [
  { id: "hiring" as const, label: "Hiring" },
  { id: "stack" as const, label: "Stack" },
];
